import get from 'lodash/get';
import isNaN from 'lodash/isNaN';
import math from 'math-precision';

import {
  FETCH_PRICING_REJECTED,
  FETCH_PRICING_FULFILLED,
  FETCH_PRICING_PENDING,
  CANCEL,
  DONE,
  SET_PRICE_PER_TICKET,
  SET_OFFER, SET_PAYOUT_FUNDING_INFO,
  SET_PREFER_OFFER,
  GET_NEW_TICKET_GROUP_TOKEN,
} from '../constants';
import { isEventEligibleFor } from '../components/utils';
import { ELIGIBILITY } from '../components/utils/constants';

export const defaultState = {
  data: null,
  valid: false,
  fetched: false,
  fetching: false,
  offer: null,
  payoutFundingInfo: null,
  fanWalletToken: '',
  sellerPrice: {
    fees: 0,
    pricePerTicket: 0,
    payout: 0,
    containsRoundOffError: false,
    isZsf: false,
  },
  preferOffer: false,
};

export default function pricingReduce(state = defaultState, action) {
  switch (action.type) {
    case DONE:
    case CANCEL:
      return {
        ...defaultState,
      };

    case SET_OFFER: {
      return {
        ...state,
        offer: action.payload.offer,
      };
    }

    case SET_PAYOUT_FUNDING_INFO: {
      return {
        ...state,
        payoutFundingInfo: action.payload.fundingInfo,
      };
    }

    case SET_PRICE_PER_TICKET: {
      const isFanToFan = isEventEligibleFor(ELIGIBILITY.F2F_EXCHANGE);

      let price = parseFloat(action.payload.pricePerTicket);

      if (isNaN(price)) {
        price = null;
      }

      const min = get(state, 'data.pricingRange.floor.amount', 0);
      const max = get(state, 'data.pricingRange.ceiling.amount', 0) === 0 ? 25000 : get(state, 'data.pricingRange.ceiling.amount');

      const { payout, fees } = calculatePayoutAndFees(
        price,
        state.data.sellerFee,
        isFanToFan,
      );

      const roundOffValue = math.round(
        calculateListingPriceAndFees(
          payout,
          state.data.sellerFee,
          isFanToFan,
        ).singleListingPrice - price,
        2,
      );

      return {
        ...state,
        valid: price !== null && min <= price && price <= max,
        sellerPrice: {
          ...state.sellerPrice,
          containsRoundOffError: roundOffValue !== 0,
          // listing Price Per Ticket, keep it as a string
          pricePerTicket: action.payload.pricePerTicket,
          payout,
          fees,
        },
      };
    }
    case GET_NEW_TICKET_GROUP_TOKEN:
      if (action.payload.body.supportCode) {
        return {
          ...state,
          ticketGroupTokenError: action.payload.body,
          ticketGroupToken: null,
        };
      }
      return {
        ...state,
        ticketGroupToken: action.payload.body,
      };


    case FETCH_PRICING_PENDING:
      return {
        ...state,
        fetching: true,
      };
    // maybe getting something from price help and then falling back to defaults otherwise
    case FETCH_PRICING_FULFILLED: {
      const isF2F = isEventEligibleFor(ELIGIBILITY.F2F_EXCHANGE);
      const payoutPriceFromService = get(action.payload.body, 'payoutPrice.amount', 0);
      const tickets = get(action.payload.body, 'item.tickets[0]', {});
      const sellerFeePercent = get(action.payload.body, 'sellerFee.percent', -1);
      let calculatedPayout;
      let calculatedFees;
      const fixedListingPrice = get(action.payload.body, 'fixedListingPrice.amount', 0);
      let pricePerTicket = fixedListingPrice; // will be 0 if not f2f/FVE
      if (payoutPriceFromService) {
        const payout = payoutPriceFromService;
        const { singleListingPrice, fees } = calculateListingPriceAndFees(
          payout,
          action.payload.body.sellerFee,
          isF2F,
        );

        pricePerTicket = singleListingPrice;
        calculatedPayout = payout;
        calculatedFees = fees;
      } else {
        const { payout, fees } = calculatePayoutAndFees(
          fixedListingPrice,
          action.payload.body.sellerFee,
          isF2F,
        );
        calculatedFees = fees;
        calculatedPayout = payout;
      }
      return {
        ...state,
        data: action.payload.body,
        valid: pricePerTicket > 0,
        fetched: true,
        fetching: false,
        sellerPrice: {
          ...state.sellerPrice,
          pricePerTicket,
          currency: get(
            tickets,
            'charges[0].currencyCode',
            get(
              tickets,
              'faceValue.currencyCode',
              get(action.payload.body, 'payoutPrice.currencyCode', 'USD'),
            ),
          ),
          payout: calculatedPayout,
          fees: calculatedFees,
          isZsf: sellerFeePercent === 0,
        },
      };
    }

    case FETCH_PRICING_REJECTED:
      return {
        ...state,
        error: action.payload,
        fetched: true,
        fetching: false,
      };

    case SET_PREFER_OFFER: {
      return {
        ...state,
        preferOffer: true,
      };
    }

    default:
      return state;
  }
}


export function getTicketCount(store) {
  return get(store, 'pricing.data.item.tickets.length', 0);
}

/**
 *
 * calculation per ticket, always
 *
 * @param {*} listingPrice
 * @param {*} param1
 */
export function calculatePayoutAndFees(listingPrice = 0, sellerFee = {}, isF2F = false) {
  const { percent, amount } = sellerFee;
  let fees = 0;

  if (isF2F) {
    fees = 0;
  } else if (percent !== undefined) {
    fees = listingPrice * (percent / 100);
  } else if (amount !== undefined) {
    fees = amount;
  } else {
    fees = listingPrice * 0.2;
  }

  fees = math.round(fees, 2);

  const payout = math.round(listingPrice - fees, 2);

  return {
    fees,
    payout,
  };
}

/**
 *
 * calculation per ticket, always
 *
 * @param {*} payout
 * @param {*} param1
 */
export function calculateListingPriceAndFees(payout = 0, { percent, amount } = {}, isF2F = false) {
  let listingPrice = 0;
  let fees = 0;
  if (isF2F) {
    fees = 0;
  } else if (percent !== undefined) {
    listingPrice = payout / (1 - (percent / 100));
    fees = listingPrice * (percent / 100);
  } else if (amount !== undefined) {
    listingPrice = payout + amount;
    fees = amount;
  } else {
    listingPrice = payout / 0.8;
    fees = listingPrice * 0.2;
  }

  return {
    fees: math.round(fees, 2),
    singleListingPrice: math.round(listingPrice, 2),
  };
}


export function getPricingRange(store) {
  return {
    min: get(store, 'pricing.data.pricingRange.floor.amount', 0),
    max: get(store, 'pricing.data.pricingRange.ceiling.amount', 3000),
  };
}

export function getPricingGuidance(store) {
  const listingCount = get(store, 'pricing.data.pricingGuidance.listingCountWithinSection', 0);

  if (listingCount > 0) {
    return {
      min: get(store, 'pricing.data.pricingGuidance.floor.amount', -1),
      max: get(store, 'pricing.data.pricingGuidance.ceiling.amount', -1),
      listingCount,
      currency: get(store, 'pricing.data.pricingGuidance.floor.currencyCode', ''),
    };
  }

  return null;
}
